/**
 * Request schemas for API-key administration.
 *
 * Every schema is `.strict()`: an unexpected property is a 400, never a silently
 * ignored field. This is the mass-assignment guard — a client cannot smuggle
 * `status`, `ownerUserId`, `secretVerifier` or `useCount` into an update by
 * adding it to the body, because those fields are not in the schemas at all and
 * strict mode rejects them outright.
 */
import { z } from "zod";
import { isKnownAction } from "./authz.js";

/**
 * A key's scopes are MODULE ACTIONS from the manifest, not entries in the old
 * flat catalogue. Validated against the manifest so a scope that no endpoint
 * could honour cannot be stored, and so renaming an action is caught here rather
 * than at request time.
 */
const apiKeyScopeSchema = z
  .string()
  .refine(isKnownAction, "not a known module action");

/** Human-facing label. Bounded so it cannot bloat a row or a log line. */
const keyNameSchema = z.string().trim().min(1).max(120);

/** IPv4/IPv6 address or CIDR block. Range validity is re-checked server-side. */
const cidrSchema = z
  .string()
  .trim()
  .max(64)
  .regex(
    /^[0-9a-fA-F:.]+(\/\d{1,3})?$/,
    "Must be an IP address or CIDR block (e.g. 203.0.113.4 or 203.0.113.0/24)",
  );

export const createApiKeySchema = z
  .object({
    name: keyNameSchema,
    description: z.string().trim().max(500).optional(),
    /**
     * Least privilege is mandatory: at least one scope, and no wildcard. The
     * server additionally rejects any scope that no endpoint actually honours.
     */
    scopes: z.array(apiKeyScopeSchema).min(1).max(64),
    /**
     * Days until expiry. `null` requests a non-expiring key — permitted, but
     * the admin UI marks it as discouraged and the server caps any explicit
     * value at API_KEY_MAX_LIFETIME_DAYS.
     */
    expiresInDays: z.number().int().min(1).max(3650).nullable().default(90),
    ipAllowlist: z.array(cidrSchema).max(32).default([]),
    /**
     * Owner. Optional — defaults to the creating administrator. Accountability
     * only: it does NOT give the key the owner's permissions.
     */
    ownerUserId: z.string().uuid().optional(),
  })
  .strict();

export const updateApiKeySchema = z
  .object({
    name: keyNameSchema.optional(),
    description: z.string().trim().max(500).nullable().optional(),
  })
  .strict()
  .refine((v) => v.name !== undefined || v.description !== undefined, {
    message: "Provide at least one field to update",
  });

export const setApiKeyScopesSchema = z
  .object({
    scopes: z.array(apiKeyScopeSchema).min(1).max(64),
  })
  .strict();

export const setApiKeyEnabledSchema = z
  .object({ enabled: z.boolean() })
  .strict();

export const rotateApiKeySchema = z
  .object({
    expiresInDays: z.number().int().min(1).max(3650).nullable().default(90),
  })
  .strict();

export const revokeApiKeySchema = z
  .object({
    reason: z.string().trim().max(500).optional(),
  })
  .strict();

export type CreateApiKeyInput = z.infer<typeof createApiKeySchema>;
export type UpdateApiKeyInput = z.infer<typeof updateApiKeySchema>;
export type SetApiKeyScopesInput = z.infer<typeof setApiKeyScopesSchema>;
export type RotateApiKeyInput = z.infer<typeof rotateApiKeySchema>;
export type RevokeApiKeyInput = z.infer<typeof revokeApiKeySchema>;
